/**
 * P2 — N2 · Competência 4 (Grandezas e medidas) · task-model H3.25.07
 *
 *   "Calcular a velocidade média de um percurso feito em trechos com velocidades
 *    diferentes (não é a média aritmética)."
 *
 * O erro clássico — tirar a média aritmética das velocidades — é o distrator
 * principal. A ANIMAÇÃO EXISTE PARA TORNAR ESSE ERRO VISÍVEL, não para punir quem o
 * comete: o aluno vê o móvel demorar mais no trecho lento, e a intuição de que "os
 * dois trechos pesam igual" quebra sozinha.
 *
 * Este item também é onde os flags de mídia ganham consequência:
 *   - `audio: { need: 'none' }` — deliberadamente SEM ÁUDIO, aplicável em sala;
 *   - `motion: 'animated'` com `staticFallbackAt` — sob `prefers-reduced-motion` a
 *     informação continua acessível, e o mesmo quadro serve à impressão.
 */

import { formatBR, roundTo } from '../../core/num.ts';
import { de, prep } from '../../core/pt.ts';
import type { Rng } from '../../core/rng.ts';
import { assembleSelectOne } from '../../engine/assemble.ts';
import { comExplicacoes, type Explicacoes } from '../../engine/explain.ts';
import { tripDiagram, type TripLeg } from '../../render/chart.ts';
import type { DifficultySourceCode } from '../../schema/difficulty.ts';
import {
  ARRED_TRUNCA,
  VEL_DISTANCIA_TOTAL_POR_TEMPO_PARCIAL,
  VEL_HARMONICA_INDEVIDA,
  VEL_MEDIA_ARITMETICA,
  VEL_MENOR_VELOCIDADE,
  VEL_PONDERA_POR_DISTANCIA,
  VEL_SEGUNDO_TRECHO_SO,
  VEL_SOMA_VELOCIDADES,
  VEL_TEMPO_TROCADO,
  VEL_UM_TRECHO_SO,
} from '../../schema/errors.ts';
import type { BuggySolver, ItemTemplate } from '../../schema/item.ts';
import { NOT_APPLICABLE, templateId } from '../../schema/item.ts';
import { br, strong, txt, type RichText } from '../../schema/richtext.ts';
import { skillCode, taskModelCode } from '../../schema/taxonomy.ts';

interface Cenario {
  /**
   * A faixa de velocidades que este veículo anda de verdade, em km/h.
   *
   * Todos os contextos sorteavam das mesmas velocidades, até 150 km/h, e saíam "um
   * ciclista a 90 km/h" e "uma van escolar a 120 km/h". Contexto implausível é ruído
   * que o item mede junto: o aluno que estranha o ciclista gasta a atenção nisso, e o
   * que não estranha aprende que qualquer número serve. A faixa tem as DUAS pontas:
   * só com o teto, saía um trem de passageiros a 20 km/h por doze horas.
   */
  readonly vMin: number;
  readonly vMax: number;
  readonly movel: string;
  readonly verbo: string;
  readonly origem: string;
  readonly meio: string;
  readonly destino: string;
}

const CENARIOS: readonly Cenario[] = [
  {
    vMin: 40,
    vMax: 90,
    movel: 'Um caminhão',
    verbo: 'percorre',
    origem: 'o centro de distribuição',
    meio: 'o entroncamento',
    destino: 'a loja',
  },
  {
    vMin: 40,
    vMax: 90,
    movel: 'Um ônibus intermunicipal',
    verbo: 'percorre',
    origem: 'a rodoviária',
    meio: 'a cidade vizinha',
    destino: 'o destino final',
  },
  {
    // Era uma van ESCOLAR, e os trechos vão de 60 a 240 km — nenhuma rota escolar tem
    // isso. Uma van de turismo, na estrada, tem.
    vMin: 30,
    vMax: 80,
    movel: 'Uma van de turismo',
    verbo: 'faz',
    origem: 'o hotel',
    meio: 'o mirante',
    destino: 'a praia',
  },
  {
    // Substitui o ciclista: nenhuma velocidade da tabela (a menor é 20 km/h) serve a
    // uma bicicleta em trechos de 60 a 240 km.
    vMin: 60,
    vMax: 120,
    movel: 'Um trem de passageiros',
    verbo: 'percorre',
    origem: 'a estação central',
    meio: 'a estação de baldeação',
    destino: 'o terminal',
  },
];

export interface Params {
  readonly cenario: Cenario;
  /** Distâncias em km. */
  readonly d1: number;
  readonly d2: number;
  /** Velocidades em km/h. */
  readonly v1: number;
  readonly v2: number;
  /** Tempos em horas (derivados; guardados para não recalcular). */
  readonly t1: number;
  readonly t2: number;
  readonly mesmaDistancia: boolean;
}

const VELOCIDADES = [30, 40, 50, 60, 75, 80, 90, 100, 120] as const;

/**
 * Amostragem RESPOSTA PRIMEIRO.
 *
 * Não sorteamos velocidades e distâncias torcendo para a média sair redonda:
 * escolhemos os TEMPOS (que são o que de fato pondera) e só então uma segunda
 * velocidade que faça a média fechar em número inteiro. A rejeição fica confinada a
 * uma escolha de uma variável só, com aceitação alta.
 */
function drawParams(rng: Rng): Params {
  const cenario = rng.pick(CENARIOS);
  const mesmaDistancia = rng.bool(0.45);
  const cabe = (v: number): boolean => v >= cenario.vMin && v <= cenario.vMax;

  if (mesmaDistancia) {
    // Caso clássico da média harmônica. Pares tabelados: rejeição ZERO.
    const pares: readonly (readonly [number, number])[] = [
      [60, 40],
      [40, 60],
      [30, 60],
      [60, 30],
      [80, 120],
      [120, 80],
      [50, 75],
      [75, 50],
      [90, 45],
    ];
    const [v1, v2] = rng.pick(pares.filter(([a, b]) => cabe(a) && cabe(b)));
    const d = rng.pick([60, 90, 120, 150, 180, 240]);
    return {
      cenario,
      d1: d,
      d2: d,
      v1,
      v2,
      t1: d / v1,
      t2: d / v2,
      mesmaDistancia: true,
    };
  }

  // TABELA, NÃO REJEIÇÃO. Ver a nota em `COMBINACOES`.
  const combo = rng.pick(COMBINACOES.filter((c) => cabe(c.v1) && cabe(c.v2)));

  return {
    cenario,
    d1: combo.v1 * combo.t1,
    d2: combo.v2 * combo.t2,
    v1: combo.v1,
    v2: combo.v2,
    t1: combo.t1,
    t2: combo.t2,
    mesmaDistancia: false,
  };
}

interface Combinacao {
  readonly t1: number;
  readonly t2: number;
  readonly v1: number;
  readonly v2: number;
}

/**
 * TODAS as combinações válidas, tabeladas uma vez — rejeição ZERO.
 *
 * A versão anterior sorteava tempos e a primeira velocidade, e só então procurava por
 * rejeição uma segunda velocidade que fechasse média inteira. Para muitos trios
 * sorteados não existe nenhuma, e a taxa de rejeição batia em 20% — acima do orçamento
 * testado. Rejeição alta não é só desperdício: é sintoma de espaço de parâmetros mal
 * desenhado, e vira travada visível quando uma turma inteira pede itens ao mesmo tempo.
 *
 * Enumerar de antemão é o mesmo princípio de "resposta primeiro" já usado nos outros
 * itens: em vez de sortear e torcer, sorteia-se de um conjunto que já satisfaz tudo.
 *
 * Condições: média inteira (a resposta não pode ser dízima) e média suficientemente
 * distante da média aritmética simples (senão o distrator principal colide com a chave).
 */
const COMBINACOES: readonly Combinacao[] = (() => {
  const out: Combinacao[] = [];
  for (const t1 of [1, 2, 3]) {
    for (const t2 of [1, 2, 3]) {
      for (const v1 of VELOCIDADES) {
        for (const v2 of VELOCIDADES) {
          if (v1 === v2) continue;
          const media = (v1 * t1 + v2 * t2) / (t1 + t2);
          if (!Number.isInteger(media)) continue;
          if (Math.abs(media - (v1 + v2) / 2) < 2) continue;
          out.push({ t1, t2, v1, v2 });
        }
      }
    }
  }
  return out;
})();

/** A chave: distância TOTAL dividida pelo tempo TOTAL. */
function solveKey(p: Params): number {
  return roundTo((p.d1 + p.d2) / (p.t1 + p.t2), 1);
}

/**
 * FAIXAS DE PRIORIDADE, não uma fila fixa.
 *
 * A faixa 1 tem o erro que dá nome ao task-model: ele aparece em toda variante.
 * A faixa 2 reúne sete erros de valor pedagógico equivalente, e o motor sorteia
 * quais entram — o que faz variar quantos distratores caem ABAIXO da chave e, com
 * isso, em que letra a chave aparece.
 *
 * Sem esse sorteio, a primeira execução da bateria mostrou a chave concentrada em
 * uma única posição em 79% dos itens: a maioria dos erros aqui produz valores MAIORES
 * que a resposta certa, então a chave caía quase sempre na segunda letra. Um aluno
 * treinado aprenderia a marcar aquela letra sem ler o item.
 */
const BUGGY: readonly BuggySolver<Params, number>[] = [
  {
    // O erro que dá nome ao task-model — sempre presente.
    error: VEL_MEDIA_ARITMETICA,
    priority: 1,
    solve: (p) => roundTo((p.v1 + p.v2) / 2, 1),
  },

  // --- faixa 2: sorteados entre si -----------------------------------------
  {
    // Pondera pelas distâncias em vez de pelos tempos. Some quando as distâncias
    // são iguais — aí coincide com a média aritmética, e fabricá-lo seria mentir.
    error: VEL_PONDERA_POR_DISTANCIA,
    priority: 2,
    solve: (p) =>
      p.mesmaDistancia ? NOT_APPLICABLE : roundTo((p.v1 * p.d1 + p.v2 * p.d2) / (p.d1 + p.d2), 1),
  },
  {
    error: VEL_UM_TRECHO_SO,
    priority: 2,
    solve: (p) => roundTo(p.d1 / (p.t1 + p.t2), 1),
  },
  {
    error: VEL_SEGUNDO_TRECHO_SO,
    priority: 2,
    solve: (p) => roundTo(p.d2 / (p.t1 + p.t2), 1),
  },
  {
    // "O percurso todo não pode ser mais rápido que o trecho mais lento."
    error: VEL_MENOR_VELOCIDADE,
    priority: 2,
    solve: (p) => roundTo(Math.min(p.v1, p.v2), 1),
  },
  {
    // Aplica a fórmula do caso de distâncias iguais fora do caso dela. Quando as
    // distâncias SÃO iguais, isso é a própria chave — logo, inaplicável.
    error: VEL_HARMONICA_INDEVIDA,
    priority: 2,
    solve: (p) =>
      p.mesmaDistancia ? NOT_APPLICABLE : roundTo((2 * p.v1 * p.v2) / (p.v1 + p.v2), 1),
  },
  {
    error: VEL_DISTANCIA_TOTAL_POR_TEMPO_PARCIAL,
    priority: 2,
    solve: (p) => roundTo((p.d1 + p.d2) / p.t1, 1),
  },
  {
    // Calcula os tempos trocando as velocidades entre os trechos.
    error: VEL_TEMPO_TROCADO,
    priority: 2,
    solve: (p) => {
      const tTrocado = p.d1 / p.v2 + p.d2 / p.v1;
      if (!(tTrocado > 0)) return NOT_APPLICABLE;
      return roundTo((p.d1 + p.d2) / tTrocado, 1);
    },
  },

  // --- faixa 3: reposição, valores mais distantes ---------------------------
  {
    error: VEL_SOMA_VELOCIDADES,
    priority: 3,
    solve: (p) => roundTo(p.v1 + p.v2, 1),
  },
  {
    error: ARRED_TRUNCA,
    priority: 3,
    solve: (p) => {
      const k = solveKey(p);
      const t = Math.trunc(k);
      return Math.abs(k - t) < 1e-9 ? NOT_APPLICABLE : roundTo(t, 1);
    },
  },
];

/**
 * Posição do móvel em função do tempo normalizado.
 *
 * PURA — o que torna `frame(0.5)` testável por snapshot, sem timing de DOM.
 * A proporção é a real: o trecho lento ocupa mais da animação, e é exatamente isso
 * que o item quer que o aluno perceba.
 */
function progressoEm(p: Params, t: number): number {
  const T = p.t1 + p.t2;
  const decorrido = Math.max(0, Math.min(1, t)) * T;
  const dTotal = p.d1 + p.d2;
  const percorrido = decorrido <= p.t1 ? p.v1 * decorrido : p.d1 + p.v2 * (decorrido - p.t1);
  return percorrido / dTotal;
}

/** Comprimento da estrada no desenho — e, portanto, da barra de tempo abaixo dela. */
const LARGURA_PERCURSO = 340;

// ---------------------------------------------------------------------------
// Explicações: a conta de cada erro, com os números desta variante
// ---------------------------------------------------------------------------

/** Sem ",0" à toa: "48 km/h", e "52,5 km/h" só quando a conta não fecha inteira. */
const kmh = (v: number): string => `${formatBR(v, Number.isInteger(v) ? 0 : 1)} km/h`;
const n0 = (x: number): string => formatBR(x, 0);
/** Horas sem zeros inúteis: "2 h", "1,5 h", "0,8 h". */
const horas = (t: number): string => `${formatBR(roundTo(t, 2))} h`;
/** "=" quando a conta fecha no número escrito; "≈" quando ele foi arredondado. */
const igual = (exato: number, escrito: number): string =>
  Math.abs(exato - escrito) < 1e-9 ? '=' : '≈';

/**
 * As explicações citam cada valor com as MESMAS casas da alternativa: "40,0 km/h" numa
 * variante em que outra alternativa é 52,5; "40 km/h" quando todas são inteiras. Por
 * isso o `build` as refaz, já sabendo as casas (ver `comConta`).
 */
function explicacoes(casas: number): Explicacoes<Params, number> {
  const kmh = (v: number): string => `${formatBR(v, casas)} km/h`;
  return {
    [VEL_MEDIA_ARITMETICA]: (p, v) => [
      txt(`Você fez a média das duas velocidades: (${n0(p.v1)} + ${n0(p.v2)}) ÷ 2 = ${kmh(v)}. `),
      txt(
        'Essa conta só daria certo se o móvel passasse o mesmo tempo nos dois trechos. Mas ele ' +
          `passou ${horas(p.t1)} no primeiro e ${horas(p.t2)} no segundo — e o trecho em que ele ` +
          'fica mais tempo pesa mais na média.',
      ),
    ],
    [VEL_PONDERA_POR_DISTANCIA]: (p, v) => {
      const exato = (p.v1 * p.d1 + p.v2 * p.d2) / (p.d1 + p.d2);
      return [
        txt(
          `Você ponderou as velocidades pelas distâncias: (${n0(p.v1)} × ${n0(p.d1)} + ` +
            `${n0(p.v2)} × ${n0(p.d2)}) ÷ (${n0(p.d1)} + ${n0(p.d2)}) ${igual(exato, v)} ${kmh(v)}. `,
        ),
        txt(
          'O peso de cada velocidade é o TEMPO em que o móvel andou com ela, não a distância. ' +
            'É por isso que a conta certa é a distância total dividida pelo tempo total.',
        ),
      ];
    },
    [VEL_UM_TRECHO_SO]: (p, v) => [
      txt(
        `Você dividiu só a distância do primeiro trecho pelo tempo total: ${n0(p.d1)} km ÷ ` +
          `${horas(p.t1 + p.t2)} ${igual(p.d1 / (p.t1 + p.t2), v)} ${kmh(v)}. `,
      ),
      txt(
        `A distância tem de ser a do percurso inteiro: ${n0(p.d1)} + ${n0(p.d2)} = ` +
          `${n0(p.d1 + p.d2)} km.`,
      ),
    ],
    [VEL_SEGUNDO_TRECHO_SO]: (p, v) => [
      txt(
        `Você dividiu só a distância do segundo trecho pelo tempo total: ${n0(p.d2)} km ÷ ` +
          `${horas(p.t1 + p.t2)} ${igual(p.d2 / (p.t1 + p.t2), v)} ${kmh(v)}. `,
      ),
      txt(
        `A distância tem de ser a do percurso inteiro: ${n0(p.d1)} + ${n0(p.d2)} = ` +
          `${n0(p.d1 + p.d2)} km.`,
      ),
    ],
    [VEL_MENOR_VELOCIDADE]: (p, v) => [
      txt(`Você respondeu a menor das duas velocidades, ${kmh(v)}. `),
      txt(
        `A velocidade média do percurso fica sempre ENTRE as duas — entre ` +
          `${n0(Math.min(p.v1, p.v2))} e ${n0(Math.max(p.v1, p.v2))} km/h —, mais perto da ` +
          'velocidade do trecho em que o móvel passa mais tempo.',
      ),
    ],
    [VEL_HARMONICA_INDEVIDA]: (p, v) => {
      const exato = (2 * p.v1 * p.v2) / (p.v1 + p.v2);
      return [
        txt(
          `Você usou a fórmula 2 × ${n0(p.v1)} × ${n0(p.v2)} ÷ (${n0(p.v1)} + ${n0(p.v2)}) ` +
            `${igual(exato, v)} ${kmh(v)}. `,
        ),
        txt(
          'Ela só vale quando os dois trechos têm a mesma distância — e aqui eles têm ' +
            `${n0(p.d1)} km e ${n0(p.d2)} km.`,
        ),
      ];
    },
    [VEL_DISTANCIA_TOTAL_POR_TEMPO_PARCIAL]: (p, v) => [
      txt(
        `Você dividiu a distância total pelo tempo de um trecho só: ${n0(p.d1 + p.d2)} km ÷ ` +
          `${horas(p.t1)} ${igual((p.d1 + p.d2) / p.t1, v)} ${kmh(v)}. `,
      ),
      txt(
        `O tempo tem de ser o do percurso inteiro: ${horas(p.t1)} + ${horas(p.t2)} = ` +
          `${horas(p.t1 + p.t2)}.`,
      ),
    ],
    [VEL_TEMPO_TROCADO]: (p, v) => {
      const tt = p.d1 / p.v2 + p.d2 / p.v1;
      return [
        txt(
          `Você calculou o tempo de cada trecho com a velocidade do outro: ${n0(p.d1)} ÷ ` +
            `${n0(p.v2)} + ${n0(p.d2)} ÷ ${n0(p.v1)} ${igual(tt, roundTo(tt, 2))} ${horas(tt)}. `,
        ),
        txt(
          `Dividindo a distância total por esse tempo, chega-se a ${kmh(v)}. Cada trecho tem de ` +
            'ser dividido pela sua própria velocidade.',
        ),
      ];
    },
    [VEL_SOMA_VELOCIDADES]: (p, v) => [
      txt(`Você somou as velocidades: ${n0(p.v1)} + ${n0(p.v2)} = ${kmh(v)}. `),
      txt(
        'Velocidades não se somam ao longo de um percurso: a média fica entre as duas, entre ' +
          `${n0(Math.min(p.v1, p.v2))} e ${n0(Math.max(p.v1, p.v2))} km/h.`,
      ),
    ],
    [ARRED_TRUNCA]: (p, v) => [
      txt(`Você cortou a parte decimal: a conta dá ${kmh(solveKey(p))}, e não ${kmh(v)}.`),
    ],
  };
}

export const velocidadeMedia: ItemTemplate<Params, number> = {
  id: templateId('h3-25-07-velocidade-media'),
  // v3: velocidades descem para baixo da estrada, e a barra de tempo passa a ter o
  // comprimento da estrada — a figura que o aluno vê mudou.
  version: 4,
  taskModel: taskModelCode('H3.25.07'),
  skills: [skillCode('H3.25')],
  axis: 'II',
  level: 'N2',
  title: 'Velocidade média em trechos com velocidades diferentes',
  responseModel: '3PL',

  difficultySources: ['steps.2', 'combined-skill'],

  params: drawParams,
  solve: solveKey,
  buggy: comExplicacoes(BUGGY, explicacoes(1)),

  variantDifficulty(p) {
    const fontes: DifficultySourceCode[] = [];
    // Distâncias iguais é o caso da média harmônica: mais difícil, porque a
    // simetria aparente reforça justamente a concepção errada.
    if (p.mesmaDistancia) fontes.push('inverse-problem');
    if (!Number.isInteger(p.t1) || !Number.isInteger(p.t2)) fontes.push('non-round-numbers');
    return fontes;
  },

  build(p, key, distractors, rng) {
    const { cenario } = p;

    const legs: readonly TripLeg[] = [
      { distancia: p.d1, velocidade: p.v1, rotulo: '1º trecho' },
      { distancia: p.d2, velocidade: p.v2, rotulo: '2º trecho' },
    ];

    const desc =
      `Esquema do percurso em dois trechos. Primeiro trecho: ${formatBR(p.d1, 0)} km a ` +
      `${formatBR(p.v1, 0)} km/h. Segundo trecho: ${formatBR(p.d2, 0)} km a ` +
      `${formatBR(p.v2, 0)} km/h. O móvel percorre o esquema no ritmo real de cada trecho, e ` +
      'uma barra de tempo, do mesmo comprimento do percurso, mostra quanto do tempo total já passou.';

    const stem: RichText = [
      txt(`${cenario.movel} ${cenario.verbo} o caminho ${de(cenario.origem)} até `),
      txt(`${cenario.destino}, passando ${prep('por', cenario.meio)}. `),
      txt(
        p.mesmaDistancia
          ? `Os dois trechos têm a mesma distância, ${formatBR(p.d1, 0)} km cada. `
          : `O primeiro trecho tem ${formatBR(p.d1, 0)} km e o segundo, ${formatBR(p.d2, 0)} km. `,
      ),
      txt(
        `A velocidade média foi de ${formatBR(p.v1, 0)} km/h no primeiro trecho e de ` +
          `${formatBR(p.v2, 0)} km/h no segundo.`,
      ),
    ];

    const comando: RichText = [
      txt('A velocidade média do percurso '),
      strong('inteiro'),
      txt(' foi de:'),
    ];

    // Todas as alternativas com as mesmas casas: inteiras quando todas são inteiras.
    const casas = [key, ...distractors.map((d) => d.value)].every((v) => Number.isInteger(v))
      ? 0
      : 1;
    const ex = explicacoes(casas);
    const comConta = distractors.map((d) => {
      const e = ex[d.error as string];
      return e ? { ...d, explanation: e(p, d.value) } : d;
    });
    const interaction = assembleSelectOne<number>({
      structure: 'E2',
      key,
      distractors: comConta,
      render: (v) => [txt(`${formatBR(v, casas)} km/h`)],
      raw: (v) => v,
      rng,
    });

    return {
      stem,
      comando,
      stimuli: [
        {
          kind: 'animated',
          motion: 'animated',
          // SEM ÁUDIO: o item é aplicável numa sala de aula comum, sem fones.
          audio: { need: 'none' },
          id: 'percurso',
          alt: desc,
          durationMs: 7000,
          loop: true,
          // Quadro mostrado sob `prefers-reduced-motion` e na impressão: o móvel
          // no fim do primeiro trecho, que é o instante informativo.
          staticFallbackAt: p.t1 / (p.t1 + p.t2),
          frame: (t) =>
            tripDiagram(legs, progressoEm(p, t), {
              title: 'Percurso em dois trechos',
              desc,
              width: LARGURA_PERCURSO,
            }),
          // A barra de tempo cobre exatamente a estrada, do início ao fim.
          timeline: { x0: 0, x1: LARGURA_PERCURSO },
          caption: [
            txt('O ponto identificado como '),
            strong('o móvel'),
            txt(' anda sobre o percurso no ritmo real de cada trecho. Logo abaixo, a '),
            strong('barra de tempo'),
            txt(' tem o mesmo comprimento do percurso e mostra quanto do tempo total já passou.'),
          ],
        },
      ],
      interaction,
      rationale: [
        strong('Resolução. '),
        txt('Velocidade média é distância total ÷ tempo total — nunca a média das velocidades.'),
        br,
        txt(
          `Tempo do 1º trecho: ${n0(p.d1)} ÷ ${n0(p.v1)} ${igual(p.t1, roundTo(p.t1, 2))} ${horas(p.t1)}`,
        ),
        br,
        txt(
          `Tempo do 2º trecho: ${n0(p.d2)} ÷ ${n0(p.v2)} ${igual(p.t2, roundTo(p.t2, 2))} ${horas(p.t2)}`,
        ),
        br,
        txt(`Média = ${n0(p.d1 + p.d2)} km ÷ ${horas(p.t1 + p.t2)} = ${kmh(key)}.`),
        br,
        txt(
          // Maior OU menor: quando o móvel passa mais tempo no trecho rápido, a média
          // aritmética fica abaixo da resposta.
          `A média aritmética daria ${kmh((p.v1 + p.v2) / 2)} — valor ${(p.v1 + p.v2) / 2 > key ? 'maior' : 'menor'}, porque ` +
            'trataria como iguais dois trechos que consumiram tempos diferentes.',
        ),
      ],
      declared: p.mesmaDistancia ? 'dificil' : 'media',
    };
  },

  validate(_built, p) {
    const k = solveKey(p);
    const lo = Math.min(p.v1, p.v2);
    const hi = Math.max(p.v1, p.v2);
    // A média TEM que ficar entre as duas velocidades. Fora disso, o gerador errou.
    if (k < lo - 1e-6 || k > hi + 1e-6) {
      return `velocidade média ${k} fora do intervalo [${lo}, ${hi}]`;
    }
    return null;
  },
};
